import type { RequestResultSetUpdateInstruction } from 'state/data/dataActions';
import type { NodeIdFilter, PropertyFilterDto, Sort } from 'state/grids/gridActions';
import type { ColumnFilter, PropertyFilter } from 'state/grids/gridState';
import { EAllowBlanks, GRID_STORE } from 'state/grids/gridState';
import type { ColumnSpecification } from 'state/selectedProject/selectedProjectHooks';
import type { SignalRActions } from 'state/signalr/SignalRActions';
import type { PartialRefreshDataRequest, QueryRequestModel } from 'state/signalr/signalrState';

export const updateRootRows = (
  signalRActions: SignalRActions,
  requests: RequestResultSetUpdateInstruction[],
  pendingEventId?: string
) => {
  const { gridSpecifications } = GRID_STORE.getState();

  const requestModels = requests.map((r) => {
    const { columnIndexByKey, nodeTraversalIdByColumnKey, columnSpecificationByIndexMap } =
      gridSpecifications[r.resultSetId];
    const requestFilters = requestFiltersParams(r.filters, nodeTraversalIdByColumnKey);

    const requestPropertyFilters = requestPropertyFiltersParams(
      r.propertyFilters,
      columnIndexByKey,
      columnSpecificationByIndexMap
    );

    const requestsSorts = requestSortParams(r.sortings, columnSpecificationByIndexMap);

    return {
      Options: {
        DataNodeId: r.rootNodesToRefresh,
        Filters: requestFilters,
        PropertyFilters: requestPropertyFilters,
        Sorts: requestsSorts
      },
      Context: {
        ProjectId: r.projectId,
        QueryId: r.queryId,
        ResultSetId: r.resultSetId,
        PendingEventId: pendingEventId,
        ProposalId: r.proposalId
      }
    } as QueryRequestModel<PartialRefreshDataRequest>;
  });

  if (requestModels.length > 0) {
    // Group requests by result set id to avoid "multiple projects in same request" error
    const requestsByResultSet = requestModels.reduce(
      (acc, request) => {
        const resultSetId = request.Context.ResultSetId;
        if (!acc[resultSetId]) {
          acc[resultSetId] = [];
        }
        acc[resultSetId].push(request);
        return acc;
      },
      {} as Record<string, QueryRequestModel<PartialRefreshDataRequest>[]>
    );

    // Make separate RequestRootRows calls for each result set
    Object.values(requestsByResultSet).forEach((projectRequests) => {
      signalRActions.RequestRootRows(projectRequests);
    });
  }
};

export function requestFiltersParams(
  filters: { [x: string]: ColumnFilter },
  nodeTraversalIdByColumnKey: { [p: string]: string }
) {
  return Object.keys(filters)
    .map((filterKey) => {
      const filter = filters[filterKey];

      return {
        NodeTraversalId: nodeTraversalIdByColumnKey[filterKey],
        Ids: filter.keys,
        AllowBlanks: filter.allowBlanks === EAllowBlanks.NotSet ? false : filter.allowBlanks === EAllowBlanks.Allow
      } as NodeIdFilter;
    })
    .filter(
      (f) => f.Ids.length > 0
      // || f.AllowBlanks !== undefined
      // TODO: handle blanks
    );
}

export function requestPropertyFiltersParams(
  propertyFilters: { [columnKey: string]: PropertyFilter },
  columnIndexByKey: { [key: string]: number },
  columnSpecificationByIndexMap: { [p: number]: ColumnSpecification }
) {
  return Object.keys(propertyFilters)
    .map((columnKey) => {
      const propertyFilter = propertyFilters[columnKey];
      const columnIndex = columnIndexByKey[columnKey];
      const columnSpecification = columnSpecificationByIndexMap[columnIndex];
      const propertyTraversalId = columnSpecification.propertyTraversal!.id;

      return {
        PropertyTraversalId: propertyTraversalId,
        Values: propertyFilter.values.filter((f) => f.selected).map((v) => v.rawValue ?? ''),
        AllowBlanks:
          propertyFilter.allowBlanks === EAllowBlanks.NotSet ? false : propertyFilter.allowBlanks === EAllowBlanks.Allow
      } satisfies PropertyFilterDto;
    })
    .filter(
      (f) => f.Values.length > 0
      // || f.AllowBlanks !== undefined
      // TODO: handle blanks
    );
}

export function requestSortParams(
  sortings: number[],
  columnSpecificationByIndexMap: { [p: number]: ColumnSpecification }
) {
  return sortings.map((sort, index) => {
    const columnSpecification = columnSpecificationByIndexMap[sort < 0 ? sort * -1 : sort];

    return {
      IsDescending: sort < 0,
      Order: index,
      PropertyTraversalId: columnSpecification.propertyTraversal!.id
    } as Sort;
  });
}
